import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PdButton from 'components/pd-button';
import { Col, Row, Layout, message, Modal } from 'antd';
import { get } from 'lodash';
import CollectionApi from 'services/collection';
import { PageSpinner } from 'components/page-spinner';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './style.scss';
const { Content } = Layout;
const { confirm } = Modal;

export function CollectionList() {
  const [collectionsActive, setCollectionsActive] = useState([]);
  const [collectionsInactive, setCollectionsInactive] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [limit] = useState(10);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  async function getCollectionActive() {
    const { data } = await CollectionApi.getCollections('active');
    const CollectionData = data.data;
    setCollectionsActive(CollectionData);
  }

  async function getCollectionInactive() {
    const { data } = await CollectionApi.getCollections('inactive');
    const CollectionData = data.data;
    setCollectionsInactive(CollectionData);
  }

  async function getCollectionList() {
    try {
      setLoadingPage(true);
      await getCollectionActive();
      await getCollectionInactive();
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }

  useEffect(() => {
    getCollectionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  async function hideCollection(id) {
    try {
      if (id) {
        await CollectionApi.hideCollection(id);
        message.success('Successfully removed collection');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getCollectionList();
    }
  }

  async function deleteCollection(id) {
    try {
      if (id) {
        await CollectionApi.deleteCollection(id);
        message.success('Successfully removed collection');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getCollectionList();
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure you want to hide this collection?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hideCollection(id);
      },
      onCancel() {},
    });
  }

  function handleDelete(id) {
    confirm({
      title: 'Are you sure you want to delete?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteCollection(id);
      },
      onCancel() {},
    });
  }

  async function handleRestore(id) {
    try {
      if (id) {
        await CollectionApi.restoreCollection(id);
        message.success('Successfully restored brand');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      getCollectionList();
    }
  }

  if (loadingPage) {
    return <PageSpinner />;
  }

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function SortableItem({ value: collection, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: collection.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginTop: 20,
      userSelect: 'none',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="pd-cms-collection--list draggable-item">
        <Row type="flex" align="middle" gutter={30}>
          <Col span={1}>
            <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
              ⋮⋮
            </div>
          </Col>
          <Col span={5} {...listeners}>
            <div className="pd-cms-collection--picture">
              <img
                className="image"
                src={collection.mobile_image}
                alt="collection preview"
              />
            </div>
          </Col>
          <Col span={10} {...listeners}>
            <div className="table-title--sort">{collection.title}</div>
          </Col>
          <Col span={2} {...listeners}>
            <div className="status-indicator">
              <span
                className={
                  collection.status !== 'inactive' ? 'active' : 'hidden'
                }>
                {displayStatus(collection.status)}
              </span>
            </div>
          </Col>
          <Col span={6}>
            <div className="button-table">
              <Row>
                <Col span={12} className="button">
                  {collection.status === 'active' ? (
                    <PdButton
                      type="danger"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHide(collection.id);
                      }}>
                      Deactivate
                    </PdButton>
                  ) : (
                    <PdButton
                      type="primary"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(collection.id);
                      }}>
                      Reactivate
                    </PdButton>
                  )}
                </Col>
                <Col span={12} className="button">
                  {collection.status === 'active' ? (
                    <Link
                      to={{
                        pathname: "/collections/edit", search: `id=${collection.id}`,
                      }}>
                      <PdButton type="primary" full style={{ height: 32 }}>
                        Edit
                      </PdButton>
                    </Link>
                  ) : (
                    <PdButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(collection.id);
                      }}
                      type="danger"
                      full
                      style={{ height: 32 }}>
                      Delete
                    </PdButton>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem key={item.id} index={index} value={item} />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  async function onSortEndActive({ oldIndex, newIndex }) {
    let list = [...collectionsActive];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);

    let payload = [];
    list.forEach((item, index) => {
      let order_attr = {
        id: item.id,
        order_level: index,
      };
      payload.push(order_attr);
    });

    try {
      setCollectionsActive(list);
      await CollectionApi.updateOrderCollection(payload);
      message.success('Successfully updated collection order');
    } catch {
      message.error('Cannot connect to server, please check connection');
    }
  }

  return (
    <Layout className="pd-cms-collection main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Collections</div>
          <Content className="pageContent">
            <Row gutter={20}>
              <Col span={17}></Col>
              <Col span={7} className="float-right">
                <Link to={'/collections/create'}>
                  <PdButton
                    type="primary"
                    outline
                    style={{ height: 32, width: 176 }}>
                    Add New Collection
                  </PdButton>
                </Link>
              </Col>
              <Col span={24}>
                <div id="drag-content-active">
                  <SortableList
                    items={collectionsActive}
                    onSortEnd={onSortEndActive}
                  />
                </div>
              </Col>

              {collectionsInactive.length > 0 && (
                <Col span={24} className="hidden-title-container">
                  <div className="pageTitle hiddenTitle">
                    Inactive Collections
                  </div>
                </Col>
              )}

              <Col span={24}>
                <div id="drag-content-inactive">
                  <SortableList items={collectionsInactive} />
                </div>
              </Col>
            </Row>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default { CollectionList };
